import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Chip,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { AdminMenuBar } from '../../utils/admin-menu-bar';
import { AdminCfQueues as AdminCfQueuesApi } from '../../../api/admin-cf-queue';

const CELL = { whiteSpace: 'nowrap', py: 0.5, px: 1 };
const HEAD_CELL = { ...CELL, fontWeight: 600 };

// Cloudflare Queues: $0.40 per million billable operations.
export const PRICE_PER_OP = 0.4 / 1_000_000;

function CfQueues() {
    const [state, setState] = useState({ loading: false, data: [], error: null });

    const refresh = useCallback(() => AdminCfQueuesApi(setState, 'month'), []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const queues = state.data ?? [];
    const apiItems = [{ label: 'Refresh', onClick: refresh }];

    return (
        <Stack direction='column' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <AdminMenuBar apiItems={apiItems} />
            <Box sx={{ overflow: 'auto', flex: 1 }}>
                {state.error && (
                    <Typography variant='caption' color='error' sx={{ display: 'block', mb: 1 }}>
                        {state.error}
                    </Typography>
                )}
                {!state.loading && !state.error && queues.length === 0 && (
                    <Typography variant='body2' color='text.secondary'>
                        No queues found.
                    </Typography>
                )}
                {(state.loading || queues.length > 0) && (
                    <Table size='small' sx={{ width: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={HEAD_CELL}>Name</TableCell>
                                <TableCell sx={HEAD_CELL}>Backlog</TableCell>
                                <TableCell sx={HEAD_CELL}>Consumer Lag</TableCell>
                                <TableCell sx={HEAD_CELL}>Operations (month)</TableCell>
                                <TableCell sx={HEAD_CELL}>Est. Cost (month)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.loading
                                ? Array.from({ length: 3 }, (_, i) => (
                                      <TableRow key={i}>
                                          {[0, 1, 2, 3, 4].map((j) => (
                                              <TableCell key={j} sx={CELL}>
                                                  <Skeleton />
                                              </TableCell>
                                          ))}
                                      </TableRow>
                                  ))
                                : queues.map((q) => (
                                      <TableRow key={q.queue_id} hover>
                                          <TableCell sx={CELL}>{q.queue_name}</TableCell>
                                          <TableCell sx={CELL}>
                                              {q.backlog_count == null ? (
                                                  '—'
                                              ) : (
                                                  <Chip
                                                      label={`${q.backlog_count} msgs`}
                                                      size='small'
                                                      color={q.backlog_count > 0 ? 'warning' : 'default'}
                                                  />
                                              )}
                                          </TableCell>
                                          <TableCell sx={CELL}>
                                              {q.lag_time_ms == null ? '—' : `${(q.lag_time_ms / 1000).toFixed(2)}s`}
                                          </TableCell>
                                          <TableCell sx={CELL}>
                                              {q.billable_operations == null
                                                  ? '—'
                                                  : q.billable_operations.toLocaleString()}
                                          </TableCell>
                                          <TableCell sx={CELL}>
                                              {q.billable_operations == null
                                                  ? '—'
                                                  : `$${(q.billable_operations * PRICE_PER_OP).toFixed(4)}`}
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
        </Stack>
    );
}

export { CfQueues };
